/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import axios from 'axios';
import { SecurityHelper } from 'sarsaparilla';
import { makeAsyncAction } from 'ui-rating-review/src/utils/async_utils';
import {
    FETCH_GATEWAY_SEARCH_FAILURE,
    FETCH_GATEWAY_SEARCH_START,
    FETCH_GATEWAY_SEARCH_SUCCESS,
    FETCH_GATEWAY_TICKET_SEARCH_FAILURE,
    FETCH_GATEWAY_TICKET_SEARCH_START,
    FETCH_GATEWAY_TICKET_SEARCH_SUCCESS,
    FETCH_GATEWAY_POI_SEARCH_FAILURE,
    FETCH_GATEWAY_POI_SEARCH_START,
    FETCH_GATEWAY_POI_SEARCH_SUCCESS,
    FETCH_GATEWAY_CAMPING_SEARCH_FAILURE,
    FETCH_GATEWAY_CAMPING_SEARCH_START,
    FETCH_GATEWAY_CAMPING_SEARCH_SUCCESS,
    FETCH_GATEWAY_ACTIVITY_PASSES_SEARCH_SUCCESS,
    FETCH_GATEWAY_ACTIVITY_PASSES_SEARCH_FAILURE,
    FETCH_GATEWAY_ACTIVITY_PASSES_SEARCH_START,
    FETCH_GATEWAY_TREE_PERMITS_SEARCH_SUCCESS,
    FETCH_GATEWAY_TREE_PERMITS_SEARCH_FAILURE,
    FETCH_GATEWAY_TREE_PERMITS_SEARCH_START,
    FETCH_GATEWAY_VENUE_RESERVATIONS_SEARCH_START,
    FETCH_GATEWAY_VENUE_RESERVATIONS_SEARCH_SUCCESS,
    FETCH_GATEWAY_VENUE_RESERVATIONS_SEARCH_FAILURE,
    FETCH_GATEWAY_VENUE_FACILITIES_SEARCH_START,
    FETCH_GATEWAY_VENUE_FACILITIES_SEARCH_SUCCESS,
    FETCH_GATEWAY_VENUE_FACILITIES_SEARCH_FAILURE,
} from '../../constants/types';
import { urlWithParams } from '../../utils';

const fetchCampingSearchEndpoint = ({ locationId, sort, startDate, endDate }) =>
    urlWithParams(`${process.env.API}/search`, {
        fq: [
            `parent_asset_id:${locationId}`,
            'entity_type:campground',
            'campsite_type_of_use:Overnight',
            'campsite_type_of_use:Day',
            'campsite_type_of_use:na',
        ],
        start_date: startDate,
        end_date: endDate,
        size: '1000',
        sort,
    });

const fetchCampingSearchApi = (request) => () =>
    axios.get(fetchCampingSearchEndpoint(request));

export const fetchCampingSearch = makeAsyncAction(
    {
        startType: FETCH_GATEWAY_CAMPING_SEARCH_START,
        successType: FETCH_GATEWAY_CAMPING_SEARCH_SUCCESS,
        failureType: FETCH_GATEWAY_CAMPING_SEARCH_FAILURE,
    },
    fetchCampingSearchApi,
    false,
    'data'
);

//fq: -entity_type:tour
//fq: campsite_type_of_use:na
//fq: campsite_type_of_use:na

const fetchTicketSearchEndpoint = ({ locationId }) =>
    urlWithParams(`${process.env.API}/search`, {
        fq: [
            `parent_asset_id:${locationId}`,
            'entity_type:tour',
            'entity_type:ticketfacility',
            'entity_type:timedentry',
            'entity_type:timedentry_tour',
            'entity_type:permit',
            'entity_type:vehiclepermit',
        ],
        size: '1000',
        ...(SecurityHelper.isCommercialAccount() && { t: '2' }),
    });

const fetchTicketSearchApi = (request) => () =>
    axios.get(fetchTicketSearchEndpoint(request));

export const fetchTicketSearch = makeAsyncAction(
    {
        startType: FETCH_GATEWAY_TICKET_SEARCH_START,
        successType: FETCH_GATEWAY_TICKET_SEARCH_SUCCESS,
        failureType: FETCH_GATEWAY_TICKET_SEARCH_FAILURE,
    },
    fetchTicketSearchApi,
    false,
    'data'
);

const fetchPOISearchEndpoint = ({ locationId }) =>
    urlWithParams(`${process.env.API}/search`, {
        fq: [
            `parent_asset_id:${locationId}`,
            'entity_type:facility',
            'entity_type:cemeteryandmemorial',
            'entity_type:library',
            'entity_type:archives',
            'entity_type:museum',
            'entity_type:kiosk',
            'entity_type:nationalfishhatchery',
            'entity_type:visitorcenter',
        ],
        size: '1000',
    });

const fetchPOISearchApi = (request) => () => axios.get(fetchPOISearchEndpoint(request));

export const fetchPOISearch = makeAsyncAction(
    {
        startType: FETCH_GATEWAY_POI_SEARCH_START,
        successType: FETCH_GATEWAY_POI_SEARCH_SUCCESS,
        failureType: FETCH_GATEWAY_POI_SEARCH_FAILURE,
    },
    fetchPOISearchApi,
    false,
    'data'
);

const fetchGatewaySearchEndpoint = ({ locationId }) =>
    urlWithParams(`${process.env.API}/search`, {
        fq: [`entity_id:${locationId}`, 'entity_type:recarea'],
        size: '1000',
    });

const fetchGatewaySearchApi = (request) => () =>
    axios.get(fetchGatewaySearchEndpoint(request));

export const fetchGatewaySearch = makeAsyncAction(
    {
        startType: FETCH_GATEWAY_SEARCH_START,
        successType: FETCH_GATEWAY_SEARCH_SUCCESS,
        failureType: FETCH_GATEWAY_SEARCH_FAILURE,
    },
    fetchGatewaySearchApi,
    false,
    'data'
);

const fetchActivityPassesSearchEndpoint = ({ locationId }) =>
    urlWithParams(`${process.env.API}/search`, {
        fq: [`parent_asset_id:${locationId}`, 'entity_type:activitypass'],
        size: '1000',
    });

const fetchActivityPassesSearchApi = (request) => () =>
    axios.get(fetchActivityPassesSearchEndpoint(request));

export const fetchActivityPassesSearch = makeAsyncAction(
    {
        startType: FETCH_GATEWAY_ACTIVITY_PASSES_SEARCH_START,
        successType: FETCH_GATEWAY_ACTIVITY_PASSES_SEARCH_SUCCESS,
        failureType: FETCH_GATEWAY_ACTIVITY_PASSES_SEARCH_FAILURE,
    },
    fetchActivityPassesSearchApi,
    false,
    'data'
);

const fetchTreePermitsSearchEndpoint = ({ locationId }) =>
    urlWithParams(`${process.env.API}/search`, {
        fq: [`parent_asset_id:${locationId}`, 'entity_type:treepermit'],
        size: '1000',
    });

const fetchTreePermitsSearchApi = (request) => () =>
    axios.get(fetchTreePermitsSearchEndpoint(request));

export const fetchTreePermitsSearch = makeAsyncAction(
    {
        startType: FETCH_GATEWAY_TREE_PERMITS_SEARCH_START,
        successType: FETCH_GATEWAY_TREE_PERMITS_SEARCH_SUCCESS,
        failureType: FETCH_GATEWAY_TREE_PERMITS_SEARCH_FAILURE,
    },
    fetchTreePermitsSearchApi,
    false,
    'data'
);

const fetchVenueFacilitySearchEndpoint = ({ locationId }) =>
    urlWithParams(`${process.env.API}/search`, {
        fq: [`parent_asset_id:${locationId}`, 'entity_type:venuereservations'],
        size: '1000',
    });

const fetchVenueFacilitiesSearchApi = (request) => () =>
    axios.get(fetchVenueFacilitySearchEndpoint(request));

export const fetchVenueFacilitiesSearch = makeAsyncAction(
    {
        startType: FETCH_GATEWAY_VENUE_FACILITIES_SEARCH_START,
        successType: FETCH_GATEWAY_VENUE_FACILITIES_SEARCH_SUCCESS,
        failureType: FETCH_GATEWAY_VENUE_FACILITIES_SEARCH_FAILURE,
    },
    fetchVenueFacilitiesSearchApi,
    false,
    'data'
);

const fetchVenueReservationSearchEndpoint = ({ locationId }) =>
    urlWithParams(`${process.env.API}/search`, {
        fq: [`parent_asset_id:${locationId}`, 'entity_type:venuereservations_venue'],
        size: '1000',
    });

const fetchVenueReservationsSearchApi = (request) => () =>
    axios.get(fetchVenueReservationSearchEndpoint(request));

export const fetchVenueReservationsSearch = makeAsyncAction(
    {
        startType: FETCH_GATEWAY_VENUE_RESERVATIONS_SEARCH_START,
        successType: FETCH_GATEWAY_VENUE_RESERVATIONS_SEARCH_SUCCESS,
        failureType: FETCH_GATEWAY_VENUE_RESERVATIONS_SEARCH_FAILURE,
    },
    fetchVenueReservationsSearchApi,
    false,
    'data'
);
